import { useEffect } from "react";

// Phones: the on-screen keyboard shrinks the *visual* viewport, but `fixed inset-0` / `100dvh` still size
// to the layout viewport, so iOS scrolls the whole page to reveal the focused field and pushes the top of a
// full-screen view (e.g. the chat header) off screen. While a full-screen view is open we mirror the visual
// viewport into CSS variables, and the view pins itself to them:
//   top: var(--vv-top, 0px); height: var(--vv-height, 100dvh)
// so its header stays at the top and its composer sits right on the keyboard, like a native app.

// Visual viewport this much shorter than the window means the on-screen keyboard is up.
const KEYBOARD_MIN_PX = 120;

let users = 0;
let frame = 0;
let detach = null;

function writeVars() {
  frame = 0;
  const vv = window.visualViewport;
  if (!vv) return;
  const root = document.documentElement.style;
  root.setProperty("--vv-height", `${Math.round(vv.height)}px`);
  root.setProperty("--vv-top", `${Math.round(vv.offsetTop)}px`);
  // With the keyboard up the home-indicator inset is hidden behind it; drop it so the composer sits flush.
  const keyboardOpen = window.innerHeight - vv.height > KEYBOARD_MIN_PX;
  root.setProperty("--vv-safe-bottom", keyboardOpen ? "0px" : "env(safe-area-inset-bottom)");
}

const schedule = () => {
  if (!frame) frame = requestAnimationFrame(writeVars);
};

function attach() {
  const vv = window.visualViewport;
  if (!vv) return () => {};
  writeVars();
  vv.addEventListener("resize", schedule);
  vv.addEventListener("scroll", schedule);
  return () => {
    vv.removeEventListener("resize", schedule);
    vv.removeEventListener("scroll", schedule);
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    const root = document.documentElement.style;
    root.removeProperty("--vv-height");
    root.removeProperty("--vv-top");
    root.removeProperty("--vv-safe-bottom");
  };
}

// Track the visual viewport while `active`. Several views may use it at once.
export function useVisualViewport(active = true) {
  useEffect(() => {
    if (!active || typeof window === "undefined") return undefined;
    users += 1;
    if (users === 1) detach = attach();
    return () => {
      users -= 1;
      if (users === 0 && detach) {
        detach();
        detach = null;
      }
    };
  }, [active]);
}

// Keep the page behind a full-screen view (menu, chat) from scrolling, so iOS can't drag it along with
// the view. Ref-counted so nested views don't unlock each other.
let locks = 0;
let saved = null;

export function useLockBodyScroll(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    locks += 1;
    if (locks === 1) {
      const { documentElement: html, body } = document;
      saved = { html: html.style.overflow, body: body.style.overflow, overscroll: body.style.overscrollBehavior };
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
      body.style.overscrollBehavior = "none";
    }
    return () => {
      locks -= 1;
      if (locks === 0 && saved) {
        const { documentElement: html, body } = document;
        html.style.overflow = saved.html;
        body.style.overflow = saved.body;
        body.style.overscrollBehavior = saved.overscroll;
        saved = null;
      }
    };
  }, [active]);
}

// Shorthand for a full-screen view on phones: follow the keyboard and freeze the page behind it.
export function useFullScreenView(active = true) {
  useVisualViewport(active);
  useLockBodyScroll(active);
}
